import type { ClipboardEvent, KeyboardEvent } from 'react';

/**
 * Keystroke + paste guard for numeric inputs.
 *
 * AntD's `InputNumber` does NOT stop an advisor typing letters. Its `parser`
 * only runs once a number can be derived from the text, so typing `ABCD` leaves
 * `ABCD` sitting in the box — measured, not assumed — and a mixed `12AB34` only
 * collapses to `1234` because the digits gave the parser something to work
 * with. Advisors reported exactly this on the RDSP amount fields.
 *
 * The parser is still worth keeping: it normalises what reaches state. This
 * guard is the other half, refusing the character at the point of entry so the
 * field never *shows* something it cannot hold.
 *
 * Deliberately permissive about what it lets through:
 *
 * - every control key (Backspace, Tab, arrows, Home/End) and every shortcut
 *   (Ctrl/Cmd/Alt combos, so Cmd+A and Cmd+V still work);
 *   only single printable characters are candidates for blocking;
 * - the comma, because thousands separators are what the currency formatter
 *   itself inserts — refusing it would fight the field's own display;
 * - whitespace in a PASTE, since values copied out of a spreadsheet routinely
 *   carry padding and the parser strips it harmlessly.
 */
export type NumericGuardOptions = {
  /** Allow a decimal point. False for an integer-only field. */
  allowDecimal?: boolean;
  /** Allow a leading minus. False when the field's own `min` rules out negatives. */
  allowNegative?: boolean;
  /**
   * Refuse any edit that would leave more than this many digits after the decimal point.
   * Money fields pass 2 (QA D-41: `1234567.567` was kept as typed). Undefined = no cap.
   */
  maxDecimals?: number;
  /**
   * Refuse any edit that would leave more than this many digits BEFORE the decimal point.
   * Undefined = no cap.
   *
   * Derived from the field's authored bounds by {@link integerDigitCap} — it is a cap on the
   * WIDTH of the figure, not on its value. A maximum of 999 admits any three digits, so 800
   * is typed and then NAMED as over the maximum; a fourth digit is what the field refuses.
   * The value rule itself lives in `numberRange.ts`, where the message comes from.
   */
  maxIntegerDigits?: number;
};

/**
 * Whether `text` — what the input WOULD hold after an edit — carries more than `max` digits
 * after its decimal point. Reads the text as displayed, so the thousands commas the currency
 * formatter inserts and any affix are ignored: only digits after the first `.` count.
 */
export function exceedsDecimals(text: string, max: number): boolean {
  const dot = text.indexOf('.');
  if (dot < 0) return false;
  return text.slice(dot + 1).replace(/\D/g, '').length > max;
}

/**
 * How many digits a figure carries before its decimal point — `999` -> 3, `10000.5` -> 5,
 * `-42` -> 2. The sign is not a digit and the decimals are `maxDecimals`' business.
 */
function integerDigitsOf(n: number): number {
  return String(Math.floor(Math.abs(n))).length;
}

/**
 * The widest figure a field with these bounds can legitimately hold, in digits — what the
 * guard caps the input at. Undefined when no cap can be derived.
 *
 * Taken from the MAXIMUM, which is what an author sets to size a box: 999 means three digits.
 * A negative minimum widens it, because that bound is reached by typing digits too — a field
 * running [-99999, 999] has to admit five of them, and capping at the maximum's three would
 * leave its own floor untypeable.
 *
 * Without a maximum there is nothing to measure, so the field stays uncapped however low its
 * minimum: an unauthored bound is not a statement about width.
 */
export function integerDigitCap(
  min: number | null | undefined,
  max: number | null | undefined,
): number | undefined {
  if (max === null || max === undefined || !Number.isFinite(max)) return undefined;
  const fromMax = integerDigitsOf(max);
  const fromMin =
    typeof min === 'number' && Number.isFinite(min) && min < 0 ? integerDigitsOf(min) : 0;
  return Math.max(fromMax, fromMin);
}

/**
 * Whether `text` — what the input WOULD hold after an edit — carries more than `max` digits
 * before its decimal point. Reads the text as displayed, so the thousands commas the currency
 * formatter inserts, any affix and the sign are all ignored: only digits ahead of the first
 * `.` count.
 */
export function exceedsIntegerDigits(text: string, max: number): boolean {
  const dot = text.indexOf('.');
  const head = dot < 0 ? text : text.slice(0, dot);
  return head.replace(/\D/g, '').length > max;
}

/** The text an input would hold once `inserted` replaces its current selection. */
function textAfterInsert(el: HTMLInputElement, inserted: string): string {
  const current = el.value ?? '';
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  return current.slice(0, start) + inserted + current.slice(end);
}

/** Characters a numeric field can legitimately receive, given its options. */
function allowedChars({
  allowDecimal = true,
  allowNegative = true,
}: NumericGuardOptions): Set<string> {
  const chars = new Set('0123456789,'.split(''));
  if (allowDecimal) chars.add('.');
  if (allowNegative) chars.add('-');
  return chars;
}

/**
 * Props to spread onto an `InputNumber`. Returns handlers rather than a
 * component so both the scalar renderer and the repeatable-row column can share
 * one rule — they render separate controls and had drifted apart before.
 */
export function numericInputGuard(options: NumericGuardOptions = {}) {
  const allowed = allowedChars(options);
  const { maxDecimals, maxIntegerDigits } = options;
  // Judged against the text the edit would PRODUCE, not the key alone: a third decimal digit
  // is refused, but the same digit typed before the point (or over a selection) is fine.
  const breaksDecimals = (el: HTMLInputElement, inserted: string) =>
    maxDecimals !== undefined && exceedsDecimals(textAfterInsert(el, inserted), maxDecimals);
  // Same reading, applied ahead of the point: a field sized by a maximum of 999 takes a third
  // digit but not a fourth, whichever end of the figure it is typed at.
  const breaksWidth = (el: HTMLInputElement, inserted: string) =>
    maxIntegerDigits !== undefined &&
    exceedsIntegerDigits(textAfterInsert(el, inserted), maxIntegerDigits);
  return {
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      // Control keys arrive with multi-character `key` names ("Backspace",
      // "ArrowLeft"); shortcuts carry a modifier. Neither inserts text.
      if (e.key.length !== 1) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (
        !allowed.has(e.key) ||
        breaksDecimals(e.currentTarget, e.key) ||
        breaksWidth(e.currentTarget, e.key)
      ) {
        e.preventDefault();
      }
    },
    onPaste: (e: ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text');
      // Reject the whole paste rather than silently keeping part of it: a
      // half-pasted amount is worse than an obviously refused one.
      const cleaned = text.replace(/\s/g, '');
      if ([...text].some((c) => !allowed.has(c) && !/\s/.test(c))) e.preventDefault();
      else if (breaksDecimals(e.currentTarget, cleaned)) e.preventDefault();
      else if (breaksWidth(e.currentTarget, cleaned)) e.preventDefault();
    },
  };
}

/**
 * `InputNumber` parser: the text on screen -> the number that reaches state. Keeps digits, the
 * point and (when allowed) a leading minus, and cuts anything past `maxDecimals`.
 *
 * The keystroke/paste guard above refuses a third decimal as it is TYPED, but a value set in one
 * go — browser autofill, an automation tool's fill, an IME — fires no keydown and no paste, so
 * `1234567.567` reached state whole (QA D-31). The parser sees every route in, so this is where
 * the cap holds; the guard stays so the field never shows a digit it is about to drop.
 */
export function numericParser({
  allowNegative = true,
  maxDecimals,
}: Pick<NumericGuardOptions, 'allowNegative' | 'maxDecimals'> = {}) {
  return (text: string | undefined): string => {
    let s = (text ?? '').replace(allowNegative ? /[^0-9.-]/g : /[^0-9.]/g, '');
    if (maxDecimals !== undefined) {
      const dot = s.indexOf('.');
      if (dot >= 0) s = s.slice(0, dot + 1 + maxDecimals);
    }
    return s;
  };
}

/**
 * `InputNumber` formatter: the number in state -> the text on screen, with thousands separators.
 * {@link numericParser}'s pair — it puts the commas in, the parser takes them back out — and the
 * reason the guard above admits `,` as a typeable character at all.
 *
 * Money only. A percentage is bounded at 100 and never reaches a grouping position, and a plain
 * number is left exactly as the advisor typed it.
 *
 * Groups the INTEGER part alone. Applying the grouping regex to the whole string also punctuates
 * a long decimal tail — `1.2345` reads as `1.2,345` — which the two-decimal cap makes unreachable
 * by typing but not by a value set in one go (autofill, a fill tool, a stored answer predating
 * the cap).
 *
 * The parameter stays exactly `string | number | undefined`: widening it to admit `null` makes
 * AntD infer `InputNumber`'s value type from `value`/`step` instead of from here, and the
 * `parser` beside it — which yields a string on purpose — then fails to typecheck. `null` is
 * still handled at runtime, since an empty cell hands one over.
 */
export function thousandsFormatter(v: string | number | undefined): string {
  if (v === undefined || v === null || v === '') return '';
  const s = String(v);
  const dot = s.indexOf('.');
  const head = dot < 0 ? s : s.slice(0, dot);
  const tail = dot < 0 ? '' : s.slice(dot);
  // `\B` keeps the sign out of it: in `-250` the gap after `-` is a word boundary, so no
  // leading comma is ever inserted.
  return head.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + tail;
}
